import { Box, Chip, Typography, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import NorthEastIcon from "@mui/icons-material/NorthEast";

interface WorkLink {
  label: string;
  value: string;
}

interface WorkItemProps {
  title: string;
  images?: string[];
  bannerLabel?: string;
  bannerGradient?: string;
  role: string;
  date: string;
  techStack: string[];
  longSummary: string;
  links: WorkLink;
  onModalOpen: (values: {
    title: string;
    images?: string[];
    bannerLabel?: string;
    bannerGradient?: string;
    role: string;
    date: string;
    techStack: string[];
    longSummary: string;
    links: WorkLink;
  }) => void;
}

const cardVariants = {
  hidden: { opacity: 0, y: 36 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const WorkItem = ({
  title,
  images,
  bannerLabel,
  bannerGradient,
  role,
  date,
  techStack,
  longSummary,
  links,
  onModalOpen,
}: WorkItemProps) => {
  const theme = useTheme();
  return (
    <Box
      component={motion.div}
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      whileHover={{ y: -8 }}
      transition={{ type: "spring", stiffness: 300, damping: 24 }}
      onClick={() =>
        onModalOpen({
          title,
          images,
          bannerLabel,
          bannerGradient,
          role,
          date,
          techStack,
          longSummary,
          links,
        })
      }
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        borderRadius: "24px",
        overflow: "hidden",
        cursor: "pointer",
        background:
          "linear-gradient(160deg, rgba(255, 255, 255, 0.92) 0%, rgba(227, 255, 231, 0.4) 45%, rgba(217, 231, 255, 0.5) 100%)",
        backdropFilter: "blur(24px) saturate(200%)",
        WebkitBackdropFilter: "blur(24px) saturate(200%)",
        border: "1px solid rgba(255, 255, 255, 0.85)",
        boxShadow: "0 10px 34px rgba(31, 38, 135, 0.16)",
        transition: "box-shadow 0.3s ease, border-color 0.3s ease",
        "&:hover": {
          boxShadow: "0 22px 45px rgba(31, 38, 135, 0.2)",
          borderColor: "rgba(255, 255, 255, 1)",
        },
        "&:hover .work-item-arrow": {
          transform: "translate(3px, -3px)",
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "170px",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            bannerGradient ?? "linear-gradient(135deg, #1f2937 0%, #0b0f19 100%)",
          [theme.breakpoints.down(600)]: {
            height: "128px",
          },
        }}
      >
        {images && images.length > 0 ? (
          <Box
            component="img"
            src={images[0]}
            alt={`${title} preview`}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: bannerGradient,
            }}
          >
            <Typography
              sx={{
                fontFamily: "inter",
                fontSize: "24px",
                fontWeight: 700,
                letterSpacing: "0.5px",
                color: "#ffffff",
              }}
            >
              {bannerLabel ?? title}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          padding: "24px",
          [theme.breakpoints.down(600)]: {
            padding: "16px",
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: "inter",
            fontSize: "21px",
            fontWeight: 700,
            color: "#111111",
            mb: "4px",
            [theme.breakpoints.down(600)]: {
              fontSize: "17px",
            },
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontFamily: "inter",
            fontSize: "14px",
            fontWeight: 500,
            color: "rgba(0, 0, 0, 0.5)",
            mb: "18px",
            [theme.breakpoints.down(600)]: {
              fontSize: "12px",
              mb: "12px",
            },
          }}
        >
          {role} · {date}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            mb: "22px",
            [theme.breakpoints.down(600)]: {
              gap: "6px",
              mb: "14px",
            },
          }}
        >
          {techStack.map((tech: string) => (
            <Chip
              key={tech}
              label={tech}
              size="small"
              sx={{
                fontFamily: "inter",
                fontSize: "12px",
                fontWeight: 500,
                color: "rgba(0, 0, 0, 0.65)",
                background: "rgba(0, 0, 0, 0.055)",
                border: "none",
                height: "26px",
                [theme.breakpoints.down(600)]: {
                  fontSize: "10.5px",
                  height: "22px",
                },
              }}
            />
          ))}
        </Box>

        <Box
          sx={{
            mt: "auto",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <Typography
            sx={{
              fontFamily: "inter",
              fontSize: "14px",
              fontWeight: 600,
              color: "#111111",
              [theme.breakpoints.down(600)]: {
                fontSize: "13px",
              },
            }}
          >
            View project
          </Typography>
          <NorthEastIcon
            className="work-item-arrow"
            sx={{
              fontSize: "15px",
              color: "#111111",
              transition: "transform 0.25s ease",
              [theme.breakpoints.down(600)]: {
                fontSize: "13px",
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default WorkItem;
